import React, { useState, useEffect } from 'react'
import {
  UserGroupIcon,
  UserIcon,
  ClipboardDocumentCheckIcon,
  DocumentIcon
} from '@heroicons/react/24/outline'
import { Modal } from '../../../components/Modal'
import { Button } from '../../../components/Button'
import { useStaff } from '../../../contexts/StaffContext'
import { useChatbot } from '../../../contexts/ChatbotContext'

const BUSINESS_MESSAGE = {
  title: 'Họp team kinh doanh',
  content: ({ fullName, store }: { fullName: string; store: string }) => (
    <div className="text-gray-900 whitespace-pre-line flex flex-col gap-2">
      <span className='font-bold'>Dear {fullName},</span>
      <span>Team {store || 'kinh doanh'} sẽ họp vào hôm nay để cập nhật kết quả Q1 và kế hoạch Q2. Thời lượng 30 phút, mọi người xem file đính kèm trước để nắm nội dung nha!</span>
      <span>⏰ Thời lượng: 30 phút</span>
      <span>📂 File: KPI-Q2-2024.pdf, Bao-cao-Q1.xlsx</span>
      <span>🔗 Link họp: <span className='text-blue-500'>meet.google.com/meetingx</span></span>
    </div>
  )
}

const HR_MESSAGE = {
  title: 'Thông báo lịch nghỉ lễ 30/4',
  content: ({ fullName, store }: { fullName: string; store: string }) => (
    <div className="text-gray-900 whitespace-pre-line flex flex-col gap-2">
      <span className='font-bold'>Dear {fullName},</span>
      <span>Phòng Nhân sự xin thông báo lịch nghỉ lễ 30/4 và 1/5 năm 2024 như sau:</span>
      <span>📅 Thời gian nghỉ: 29/4 - 1/5/2024</span>
      <span>📝 Lưu ý: Các team sắp xếp công việc, bàn giao trước khi nghỉ lễ</span>
      <span>📎 File: lich-nghi-le.pdf</span>
    </div>
  )
}

interface ModalActionProps {
  onClose: () => void
  onActionSelect: (actionType: string) => void
  selectedAction: string | null
}

interface ModalContentProps {
  message: any
  modalActions: ModalActionProps
  isOpen?: boolean
}

export const InternalMessageModal: React.FC<ModalContentProps> = ({ 
  message, 
  modalActions = {
    onClose: () => {},
    onActionSelect: () => {},
    selectedAction: null
  },
  isOpen = false
}) => {
  const { staffInfo } = useStaff()
  const { setIsChatbotVisible } = useChatbot()
  const messageContent = message?.department === 'hr' ? HR_MESSAGE : BUSINESS_MESSAGE
  const [showContent, setShowContent] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setIsChatbotVisible(false)
      // Đặt thời gian để người dùng đọc nội dung (khoảng 3 giây)
      const timer = setTimeout(() => {
        setShowContent(true)
      }, 1000)
      return () => clearTimeout(timer)
    } else {
      setIsChatbotVisible(true)
    }
  }, [isOpen, setIsChatbotVisible])

  const footer = (
    <div className="flex justify-center w-full">
      <Button
        variant="primary"
        onClick={modalActions.onClose}
        className="w-full"
        disabled={!showContent}
      >
        Đã nhận thông tin nhé!
      </Button>
    </div>
  )

  return (
    <Modal 
      isOpen={isOpen}
      onClose={modalActions.onClose}
      title={messageContent.title}
      footer={footer}
    >
      <div className="space-y-6">
        {/* Nội dung chính */}
        <div className="bg-emerald-50 bg-gray-100 text-gray-400 rounded-lg p-4 animate-fade-in">
          {messageContent.content({ 
            fullName: staffInfo.fullName,
            store: staffInfo.store
          })}
        </div>

        {/* Chi tiết bổ sung */}
        <div className="space-y-3 animate-fade-in">
          {/* Người gửi */}
          <div className="flex items-center space-x-2 text-gray-700">
            <UserIcon className="w-5 h-5 text-gray-600" />
            <div className="flex items-center space-x-2">
              <span className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded text-xs">
                {message?.department === 'hr' ? 'Nhân sự' : 'Quản lý'}
              </span>
            </div>
          </div>

          {/* Nhóm nhận */}
          {message?.recipients && (
            <div className="flex items-start space-x-2 text-gray-700">
              <UserGroupIcon className="w-5 h-5 text-gray-600 mt-0.5" />
              <div className="space-y-1">
                <div className="flex flex-wrap gap-2">
                  {message.recipients.map((recipient: string, index: number) => (
                    <span key={index} className="px-2 py-1 bg-blue-100 text-blue-600 rounded-full text-xs">
                      {recipient}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* File đính kèm nếu có */}
          {message?.attachments && message.attachments.length > 0 && (
            <div className="flex items-start space-x-2 text-gray-700">
              <ClipboardDocumentCheckIcon className="w-5 h-5 text-gray-600 mt-0.5" />
              <div className="space-y-1">
                <div className="flex flex-wrap gap-2">
                  {message.attachments.map((file: string, index: number) => (
                    <span key={index} className="px-2 py-1 bg-blue-50 text-blue-600 rounded-full text-xs flex items-center">
                      <DocumentIcon className="w-4 h-4 mr-1" />
                      {file}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
} 